import { Area, AreaChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts'

import { DashboardModel } from '@/types'
import { useBusiness } from '@/contexts/BusinessContext'

import { ChartCard, ChartLegend, ChartTooltip, chartAxis, chartGrid, compactNumber } from './chart-kit'

interface SalesChartProps {
  data: DashboardModel.ChartData[]
  className?: string
}

/** Revenue over the API's chart period: the total as the headline, average and best period in the legend. */
export const SalesChart = ({ data, className }: SalesChartProps) => {
  const { formatCurrency } = useBusiness()
  const total = data.reduce((sum, d) => sum + (d.total || 0), 0)
  const best = data.reduce<DashboardModel.ChartData | null>((b, d) => (!b || d.total > b.total ? d : b), null)
  const line = 'hsl(var(--chart-1))'

  return (
    <ChartCard
      title="Revenue"
      value={formatCurrency(total)}
      subheader={data.length ? `${data[0].name} – ${data[data.length - 1].name}` : undefined}
      className={className}
      empty={!data.length || total === 0}
      legend={
        <ChartLegend
          items={[
            { label: 'Average', value: formatCurrency(Math.round(data.length ? total / data.length : 0)), color: 'hsl(var(--chart-2))' },
            ...(best ? [{ label: `Best (${best.name})`, value: formatCurrency(best.total), color: line }] : []),
          ]}
        />
      }
      table={{ columns: ['Period', 'Revenue'], rows: data.map((d) => [d.name, formatCurrency(d.total)]) }}
    >
      <AreaChart data={data} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
        <defs>
          <linearGradient id="revenueFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={line} stopOpacity={0.16} />
            <stop offset="100%" stopColor={line} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid {...chartGrid} />
        <XAxis dataKey="name" {...chartAxis} dy={8} />
        <YAxis {...chartAxis} tickFormatter={compactNumber} width={48} />
        <Tooltip content={<ChartTooltip format={formatCurrency} />} cursor={{ stroke: 'hsl(var(--chart-axis) / 0.4)' }} />
        <Area type="monotone" dataKey="total" stroke={line} strokeWidth={2} fill="url(#revenueFill)" />
      </AreaChart>
    </ChartCard>
  )
}
